import {
    Component,
    EventEmitter,
    OnDestroy,
    OnInit,
    Output,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { ButtonComponent } from "@components/button/button.component";
import { SimpleSelectorComponent } from "@components/simple-selector/simple-selector.component";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";
import { NgbModal, NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { Subject } from "rxjs";
import { map, switchMap, takeUntil } from "rxjs/operators";
import { Planeringsstatus } from "~app/common/models/constants";
import { canEdit } from "~app/role-permissions";
import { ProduktionsstatusPipe } from "../../../../common/pipes/produktionsstatus.pipe";
import { ATGARD_PLANERINGSSTATUS, Atgard, IAtgardstyper } from "../../models/atgard-models";
import { Avverkningsanmalan } from "../../models/avverkningsanmalan-models";
import { Kontrakt } from "../../models/kontrakt-models";
import { KontraktUtskriftValues } from "../../models/utskrift/utskriftValues-models";
import { AtgardMediatorService } from "../../services/atgard-mediator.service";
import { AvverkningsanmalanMediatorService } from "../../services/avverkningsanmalan-mediator.service";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { UtskriftMediatorService } from "../../services/utskrift-mediator.service";
import { setDefaultUtskriftValues } from "../../utils/utskrift/utskrift-utilities";
import { AtgardTypDialogComponent } from "./atgard-typ-dialog/atgard-typ-dialog.component";
@Component({
    selector: "rask-atgard-status",
    templateUrl: "./atgard-status.component.html",
    styleUrls: ["./atgard-status.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SimpleSelectorComponent,
        TextmultilineComponent,
        ActionButtonComponent,
        ButtonComponent,
        ProduktionsstatusPipe
    ]
})

export class AtgardStatusComponent implements OnInit, OnDestroy {
    atgard: Atgard;
    @Output() planeringsstatusChange: EventEmitter<string> = new EventEmitter<string>();

    protected atgardPlaneringsstatusLista: any[];
    protected kontraktReadonly = false;

    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly avverkningsanmalanMediatorService = inject(AvverkningsanmalanMediatorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly modalService = inject(NgbModal);
    private readonly ngDestroyed$ = new Subject<void>();
    private readonly utskriftMediatorService = inject(UtskriftMediatorService);

    private atgarder: Atgard[];
    private atgardsTyperInGrupp: any[];
    private avverkningsAnmalan: Avverkningsanmalan;
    private kontrakt: Kontrakt;
    private utskriftValues: KontraktUtskriftValues;

    private readonly canEdit = canEdit();

    constructor() {
        const isDisabled = (status: string) =>
            status === Planeringsstatus.Tillgangligt
            && !this.canEdit();

        this.atgardPlaneringsstatusLista = ATGARD_PLANERINGSSTATUS.map((value) => ({
            text: value,
            value,
            disabled: isDisabled(value)
        }));
    }

    protected get isDirty(): boolean {
        return this.kontraktMediatorService.isDirty();
    }

    protected get showChangeButton() {
        return !this.kontraktReadonly &&
            this.kontrakt?.isRundvirke &&
            !this.kontrakt?.isLeveransvirke &&
            !this.kontrakt?.skickadTillVacs &&
            !this.atgard.isTillgangligt &&
            !this.atgard.isUtfort &&
            !this.isDirty;
    }

    ngOnDestroy(): void {
        this.ngDestroyed$.next();
        this.ngDestroyed$.complete();
    }

    ngOnInit(): void {
        this.kontraktMediatorService.kontrakt$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(k => {
                this.kontrakt = k;
            });

        this.atgardMediatorService.atgard$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(atgard => {
                this.atgard = atgard;
            });

        this.kontraktMediatorService.atgarder$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(atgarder => {
                this.atgarder = atgarder;
            });

        this.kontraktMediatorService.isReadonly$
            .pipe(
                map(readonly => readonly || !this.canEdit()),
                takeUntil(this.ngDestroyed$))
            .subscribe(readonly => this.kontraktReadonly = readonly);

        this.kontraktMediatorService.atgardsTyper$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(typ => {
                this.getAtgardstyperInGrupp(typ);
            });

        this.utskriftMediatorService.utskriftValues$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(utskrift => {
                this.utskriftValues = utskrift;
            });

        this.avverkningsAnmalan = this.avverkningsanmalanMediatorService.avverkningsanmalan;
    }

    protected onPlaneringsstatusChange(value: string): void {
        this.planeringsstatusChange.emit(value);
    }

    protected onRemoveAtgard(): void {
        if (this.kontraktReadonly) {
            return;
        }
        this.atgardMediatorService.removeAtgard();
    }

    protected openchangeTypeDialog() {
        const options = {
            keyboard: false,
            backdrop: "static",
            size: "md"
        } as NgbModalOptions;
        const modalRef = this.modalService.open(AtgardTypDialogComponent, options);
        const ref = (modalRef.componentInstance as AtgardTypDialogComponent);
        ref.atgard = this.atgard;
        ref.atgardsTyper = this.atgardsTyperInGrupp;
        ref.avverkningsAnmalan = this.avverkningsAnmalan;

        (modalRef.result as Promise<string>)
            .then(newAtgardstyp => {
                this.atgard.atgardstyp = newAtgardstyp;
                const atgarder = this.atgarder;
                atgarder.find(a => a.guid === this.atgard.guid).atgardstyp = newAtgardstyp;

                const changeType$ = this.kontraktMediatorService.sendChangeAtgardTypCommand(this.atgard.guid, newAtgardstyp);
                const reload$ = this.kontraktMediatorService.triggerReloadKontrakt();

                changeType$.pipe(
                    switchMap(() => reload$)
                ).subscribe(() => {
                    setDefaultUtskriftValues(this.utskriftValues, this.kontrakt, atgarder);
                    this.kontraktMediatorService.sendResetUtskriftCommand(this.utskriftValues);
                });
            },
            (error) => {
                //
            });
    }

    private getAtgardstyperInGrupp(typer: IAtgardstyper): void {
        const grupp = typer.atgardstyper.find(x => x.kod === this.atgard.atgardstyp).grupp;
        this.atgardsTyperInGrupp = typer.atgardstyper
            .filter(x => x.grupp === grupp)
            .map(t => ({
                value: t.kod,
                text: t.namn,
                grupp: t.grupp
            }));
    }
}
